import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { AuditLogQuery } from '@/api/audit/auditApi.types';
import { ActivityEntryRow } from '@/components/common/ActivityLog/ActivityEntryRow';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import {
  ACTIVITY_LOG_CLIENT_QUERY_PARAMETER,
  ACTIVITY_LOG_TAB_QUERY_PARAMETER,
  ACTIVITY_LOG_TAB_QUERY_VALUE,
  CLIENT_ACCESS_HISTORY_PREVIEW_SIZE,
} from '@/constants/activityLog';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useWorkspaceAuditLogsQuery } from '@/hooks/useWorkspaceAuditLogsQuery';

interface AccessHistoryPanelProps {
  clientId: string;
}

function buildClientAccessHistoryQuery(clientId: string): AuditLogQuery {
  return {
    page: 1,
    pageSize: CLIENT_ACCESS_HISTORY_PREVIEW_SIZE,
    search: '',
    category: null,
    clientId,
    occurredFrom: null,
    occurredUntil: null,
  };
}

function buildFullHistoryLink(clientId: string): string {
  const searchParameters = new URLSearchParams({
    [ACTIVITY_LOG_TAB_QUERY_PARAMETER]: ACTIVITY_LOG_TAB_QUERY_VALUE,
    [ACTIVITY_LOG_CLIENT_QUERY_PARAMETER]: clientId,
  });
  return APPLICATION_ROUTES.workspaceOrganization + '?' + searchParameters.toString();
}

export function AccessHistoryPanel({ clientId }: AccessHistoryPanelProps) {
  const { t } = useTranslation('activity');
  const historyQuery = useWorkspaceAuditLogsQuery(buildClientAccessHistoryQuery(clientId), true);
  const history = historyQuery.data;

  return (
    <Card as="section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-zooindigo">{t('clientHistory.title')}</h2>
          <p className="text-xs text-zoomuted mt-0.5">{t('clientHistory.subtitle')}</p>
        </div>
        {history !== undefined && history.totalItems > 0 ? (
          <Link
            to={buildFullHistoryLink(clientId)}
            className="inline-flex items-center gap-1 text-xs font-bold text-zooprimary hover:underline"
          >
            {t('clientHistory.seeAll')}
            <Icon name="ArrowRight" />
          </Link>
        ) : null}
      </div>
      {historyQuery.isLoading === true ? (
        <div className="py-6 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : historyQuery.isError === true || history === undefined ? (
        <QueryErrorState
          onRetry={function retryHistory() {
            historyQuery.refetch();
          }}
        />
      ) : history.items.length === 0 ? (
        <p className="text-[13px] text-zoomuted pt-3">{t('clientHistory.empty')}</p>
      ) : (
        <ul className="divide-y divide-zooborder mt-1">
          {history.items.map(function renderEntry(entry) {
            return (
              <ActivityEntryRow
                key={entry.id}
                entry={entry}
                showWorkspace={false}
                showClient={false}
                compact
              />
            );
          })}
        </ul>
      )}
    </Card>
  );
}
